/* eslint-disable max-lines-per-function, max-lines */
import { PIN_COLORS } from "@design-lab/entities/pinning";
import { ConfirmActionModal } from "@design-lab/features/design-artwork/ui/ConfirmActionModal";
import { processTextWithHexCodes } from "@design-lab/shared/ui/MarkdownContent";
import { Card, CardContent } from "@design-lab/shared/ui/card";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { ArrowLeft, ChevronDown, ChevronUp, Loader2, Pencil, Pin, Trash2 } from "lucide-react";
import { EditPinSummaryModal } from "./EditPinSummaryModal";
import { PinClarifications } from "./PinClarifications";
import { PinComposer } from "./PinComposer";
import { PinMessages } from "./PinMessages";
import { PinReadyCard } from "./PinReadyCard";
import { usePinningPanel } from "./usePinningPanel";

export function PinningPanel() {
  const {
    isDesktop,
    pins,
    activePinId,
    activePin,
    composerText,
    clarificationResponses,
    removingPinIds,
    pendingDeletePinId,
    editingPinId,
    editingPin,
    isUpdatingPinInstruction,
    mobileView,
    isPanelExpanded,
    unansweredAbove,
    unansweredBelow,
    textareaRef,
    pendingCursorPositionRef,
    clarificationScrollRef,
    messagesScrollRef,
    pinsContainerRef,
    pendingPillClarifications,
    selectedFontValue,
    selectedColorValue,
    hasAnyClarificationResponse,
    lastAiMessageWithClarifications,
    setActivePinId,
    setComposerText,
    setMobileView,
    setIsPanelExpanded,
    setHoveredPinId,
    setHoveredPinSource,
    handleDeletePin,
    openDeletePinModal,
    closeDeletePinModal,
    confirmDeletePin,
    openEditPinModal,
    closeEditPinModal,
    handleUpdatePinInstruction,
    handleSelectClarificationOption,
    handleToggleCustomInput,
    handleCustomInputChange,
    handleSubmit,
    isComposerTextValid,
    calculateUnansweredPositions,
    scrollToFirstUnanswered,
    trackPinSelected
  } = usePinningPanel();

  const renderMobileContent = () => {
    if (pins.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-4 text-center text-gray-500">
          <Pin className="mb-3 h-12 w-12 -rotate-45 text-gray-300" />
          <p className="max-w-[260px] text-sm leading-relaxed">
            No instructions added yet. Please click anywhere on the image to drop a pin and add
            instructions for our designers.
          </p>
        </div>
      );
    }

    if (mobileView === "detail" && activePin) {
      const isActiveChat = activePin.state !== "ready" && activePin.state !== "unsupported";
      return (
        <div
          className={`flex flex-col overflow-hidden rounded-lg border border-blue-200 bg-blue-50/30 ${
            isActiveChat ? "h-full" : "max-h-full"
          }`}
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-blue-200 px-2 py-1.5">
            <div className="flex items-center gap-1 font-medium">
              {(activePin.state === "ready" || activePin.state === "unsupported") && (
                <button
                  type="button"
                  className="rounded p-0.5 hover:bg-gray-100"
                  onClick={() => {
                    setMobileView("list");
                    setActivePinId(null);
                  }}
                  data-testid="pin-back-button"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                </button>
              )}
              <span className="text-xs font-semibold text-gray-900">
                <span
                  style={{
                    color:
                      PIN_COLORS[pins.findIndex(p => p.id === activePin.id) % PIN_COLORS.length]
                  }}
                >
                  Pin {pins.findIndex(p => p.id === activePin.id) + 1}
                </span>
                : <span className="font-normal text-gray-500">What would you like to do?</span>
              </span>
            </div>
            <div className="flex items-center gap-1">
              {/* Expand/collapse button - only for active chats */}
              {isActiveChat && (
                <button
                  type="button"
                  data-testid="pin-panel-expand-toggle"
                  aria-label={isPanelExpanded ? "Collapse panel" : "Expand panel"}
                  onClick={() => setIsPanelExpanded(!isPanelExpanded)}
                  className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  {isPanelExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronUp className="h-4 w-4" />
                  )}
                </button>
              )}
              {activePin.state === "ready" && activePin.instruction && (
                <Button
                  data-testid={`pin-edit-${activePin.id}`}
                  aria-label={`Edit pin ${activePin.id}`}
                  onPress={() => openEditPinModal(activePin.id)}
                  variant="icon"
                  className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                data-testid={`pin-delete-${activePin.id}`}
                aria-label={`Delete pin ${activePin.id}`}
                onPress={() => {
                  if (activePin.state === "ready" || activePin.state === "unsupported") {
                    openDeletePinModal(activePin.id);
                  } else {
                    handleDeletePin(activePin.id);
                  }
                }}
                variant="icon"
                className="rounded p-0.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                isDisabled={removingPinIds.has(activePin.id)}
              >
                {removingPinIds.has(activePin.id) ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Trash2 className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          </div>

          {/* Scrollable messages area */}
          <div ref={messagesScrollRef} className="min-h-0 flex-1 overflow-y-auto">
            {(activePin.messages.length > 0 ||
              (activePin.state === "ready" && activePin.instruction)) && (
              <div className="px-2 py-1.5">
                <div className="flex flex-col gap-2">
                  {activePin.state === "ready" && activePin.instruction ? (
                    <PinReadyCard instruction={activePin.instruction} compact />
                  ) : (
                    <PinMessages
                      messages={activePin.messages}
                      isProcessing={activePin.state === "processing"}
                      thinkingText={activePin.thinkingText}
                      compact
                    />
                  )}
                </div>
              </div>
            )}

            {activePin.state === "needs_clarification" && pendingPillClarifications.length > 0 && (
              <div className="px-2 pb-2">
                <PinClarifications
                  clarifications={pendingPillClarifications}
                  responses={clarificationResponses}
                  onSelectOption={handleSelectClarificationOption}
                  onToggleCustomInput={handleToggleCustomInput}
                  onCustomInputChange={handleCustomInputChange}
                  isDisabled={false}
                  textInfo={lastAiMessageWithClarifications?.textInfo}
                  selectedFont={selectedFontValue}
                  selectedColor={selectedColorValue}
                  scrollRef={clarificationScrollRef}
                  onScroll={calculateUnansweredPositions}
                  unansweredAbove={unansweredAbove}
                  unansweredBelow={unansweredBelow}
                  onScrollToUnanswered={scrollToFirstUnanswered}
                  compact
                  onSubmit={handleSubmit}
                />
              </div>
            )}
          </div>

          {/* Composer fixed at bottom - only for active chats */}
          {activePin.state !== "ready" && (
            <div className="shrink-0 border-t border-blue-200 p-2">
              <PinComposer
                value={composerText}
                onChange={setComposerText}
                onSubmit={handleSubmit}
                onCancel={() => {
                  handleDeletePin(activePin.id);
                }}
                canSubmit={hasAnyClarificationResponse || isComposerTextValid(composerText)}
                isProcessing={activePin.state === "processing"}
                isRemoving={removingPinIds.has(activePin.id)}
                textareaRef={textareaRef}
                onNewline={pos => {
                  pendingCursorPositionRef.current = pos;
                }}
                compact
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {pins.map((pin, index) => {
          const isCompleted = pin.state === "ready" || pin.state === "unsupported";
          const hasInstruction = pin.instruction && pin.state === "ready";

          return (
            <div
              key={pin.id}
              className="flex cursor-pointer items-start justify-between gap-2 rounded-lg border border-gray-200 bg-white p-2.5 transition-colors hover:bg-gray-50"
              onClick={() => {
                setActivePinId(pin.id);
                setMobileView("detail");
                trackPinSelected(pin.id, activePinId, pin.state);
              }}
            >
              <div className="flex-1">
                <span
                  className="text-xs font-semibold"
                  style={{ color: PIN_COLORS[index % PIN_COLORS.length] }}
                >
                  Pin {index + 1}
                </span>
                {hasInstruction ? (
                  <p className="mt-1 line-clamp-1 text-sm text-gray-700">
                    {processTextWithHexCodes(pin.instruction || "")}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-gray-500">
                    {isCompleted ? "Completed" : "What would you like to do?"}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1">
                {hasInstruction && (
                  <Button
                    data-testid={`pin-edit-list-${pin.id}`}
                    aria-label={`Edit pin ${pin.id}`}
                    onPress={() => openEditPinModal(pin.id)}
                    variant="icon"
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  data-testid={`pin-delete-list-${pin.id}`}
                  aria-label={`Delete pin ${pin.id}`}
                  onPress={() => {
                    isCompleted ? openDeletePinModal(pin.id) : handleDeletePin(pin.id);
                  }}
                  variant="icon"
                  className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                  isDisabled={removingPinIds.has(pin.id)}
                >
                  {removingPinIds.has(pin.id) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Mobile layout
  if (!isDesktop) {
    return (
      <>
        <div className="flex h-full flex-col overflow-hidden bg-white">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
            {renderMobileContent()}
          </div>
        </div>
        <ConfirmActionModal
          isOpen={pendingDeletePinId !== null}
          onClose={closeDeletePinModal}
          onConfirm={confirmDeletePin}
          title="Delete pin?"
          message="Are you sure you want to delete the pin?"
          confirmText="Delete"
          cancelText="Cancel"
        />
        <EditPinSummaryModal
          isOpen={editingPinId !== null}
          onClose={closeEditPinModal}
          onSave={handleUpdatePinInstruction}
          pinIndex={editingPin ? pins.findIndex(p => p.id === editingPin.id) + 1 : 0}
          initialInstruction={editingPin?.instruction || ""}
          isLoading={isUpdatingPinInstruction}
        />
      </>
    );
  }

  // Desktop layout
  return (
    <>
      <Card className="flex h-full w-full max-w-[700px] flex-col border-none shadow-none lg:border lg:shadow">
        <CardContent className="flex flex-1 flex-col overflow-hidden p-0">
          <div ref={pinsContainerRef} className="flex flex-1 flex-col overflow-y-auto p-4">
            {pins.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center text-center text-gray-500">
                <Pin className="mb-4 h-16 w-16 -rotate-45 text-gray-300" />
                <p className="max-w-[280px] text-sm leading-relaxed">
                  No instructions added yet. Please click anywhere on the image to drop a pin and
                  add instructions for our designers.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-4 pb-4">
                {pins.map((pin, index) => {
                  const isActive = pin.id === activePinId;
                  const isProcessing = pin.state === "processing";
                  const isCompleted = pin.state === "ready" || pin.state === "unsupported";
                  const shouldConfirmDelete = isCompleted;

                  return (
                    <div
                      key={pin.id}
                      data-pin-id={pin.id}
                      className={`flex flex-col gap-3 rounded-lg border p-4 transition-colors ${
                        isActive ? "border-blue-200 bg-blue-50/30" : "border-gray-200 bg-white"
                      }`}
                      onMouseEnter={() => {
                        if (isCompleted) {
                          setHoveredPinId(pin.id);
                          setHoveredPinSource("panel");
                        }
                      }}
                      onMouseLeave={() => {
                        if (isCompleted) {
                          setHoveredPinId(null);
                          setHoveredPinSource(null);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div
                          className="flex cursor-pointer items-center gap-2 font-medium"
                          onClick={() => {
                            const isActivePinNotReady =
                              activePin &&
                              activePin.state !== "ready" &&
                              activePin.state !== "unsupported";
                            if (isActivePinNotReady) {
                              return;
                            }

                            const previousPinId = activePinId;
                            const newPinId = isActive ? null : pin.id;
                            setActivePinId(newPinId);

                            if (newPinId) {
                              trackPinSelected(newPinId, previousPinId, pin.state);
                            }
                          }}
                        >
                          <span className="text-sm font-semibold text-gray-900">
                            <span style={{ color: PIN_COLORS[index % PIN_COLORS.length] }}>
                              Pin {index + 1}
                            </span>
                            :{" "}
                            <span className="font-normal text-gray-500">
                              What would you like to do?
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {pin.state === "ready" && pin.instruction && (
                            <Button
                              data-testid={`pin-edit-${pin.id}`}
                              aria-label={`Edit pin ${pin.id}`}
                              onPress={() => openEditPinModal(pin.id)}
                              variant="icon"
                              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            data-testid={`pin-delete-${pin.id}`}
                            aria-label={`Delete pin ${pin.id}`}
                            onPress={() =>
                              shouldConfirmDelete
                                ? openDeletePinModal(pin.id)
                                : handleDeletePin(pin.id)
                            }
                            variant="icon"
                            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                            isDisabled={removingPinIds.has(pin.id)}
                          >
                            {removingPinIds.has(pin.id) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      {(pin.messages.length > 0 || (pin.state === "ready" && pin.instruction)) && (
                        <div className="flex flex-col gap-4">
                          {pin.state === "ready" && pin.instruction ? (
                            <PinReadyCard instruction={pin.instruction} />
                          ) : (
                            <PinMessages
                              messages={pin.messages}
                              isProcessing={pin.state === "processing"}
                              thinkingText={pin.thinkingText}
                            />
                          )}
                        </div>
                      )}

                      {isActive && pin.state !== "ready" && (
                        <div className={pin.messages.length > 0 ? "mt-2" : ""}>
                          {pin.state === "needs_clarification" &&
                            pendingPillClarifications.length > 0 && (
                              <PinClarifications
                                onSubmit={handleSubmit}
                                clarifications={pendingPillClarifications}
                                responses={clarificationResponses}
                                onSelectOption={handleSelectClarificationOption}
                                onToggleCustomInput={handleToggleCustomInput}
                                onCustomInputChange={handleCustomInputChange}
                                isDisabled={isProcessing}
                                textInfo={lastAiMessageWithClarifications?.textInfo}
                                selectedFont={selectedFontValue}
                                selectedColor={selectedColorValue}
                                scrollRef={clarificationScrollRef}
                                onScroll={calculateUnansweredPositions}
                                unansweredAbove={unansweredAbove}
                                unansweredBelow={unansweredBelow}
                                onScrollToUnanswered={scrollToFirstUnanswered}
                              />
                            )}
                          <PinComposer
                            value={composerText}
                            onChange={setComposerText}
                            onSubmit={handleSubmit}
                            onCancel={() =>
                              shouldConfirmDelete
                                ? openDeletePinModal(pin.id)
                                : handleDeletePin(pin.id)
                            }
                            canSubmit={
                              hasAnyClarificationResponse || isComposerTextValid(composerText)
                            }
                            isProcessing={isProcessing}
                            isRemoving={removingPinIds.has(pin.id)}
                            textareaRef={textareaRef}
                            onNewline={pos => {
                              pendingCursorPositionRef.current = pos;
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      <ConfirmActionModal
        isOpen={pendingDeletePinId !== null}
        onClose={closeDeletePinModal}
        onConfirm={confirmDeletePin}
        title="Delete pin?"
        message="Are you sure you want to delete the pin?"
        confirmText="Delete"
        cancelText="Cancel"
      />
      <EditPinSummaryModal
        isOpen={editingPinId !== null}
        onClose={closeEditPinModal}
        onSave={handleUpdatePinInstruction}
        pinIndex={editingPin ? pins.findIndex(p => p.id === editingPin.id) + 1 : 0}
        initialInstruction={editingPin?.instruction || ""}
        isLoading={isUpdatingPinInstruction}
      />
    </>
  );
}
